import React from "react";
import { Icon } from "semantic-ui-react";
import styles from "./ImagesPreview.module.scss";
import { useIntl } from "react-intl";
import { PreviewPhoto } from "../../../types/siteTypes";

type Props = {
  photos: PreviewPhoto[];
  onDelete: (file: PreviewPhoto) => void;
  loading: number;
  onReorder?: (photos: PreviewPhoto[]) => void;
};

const ImagesPreview: React.FC<Props> = ({
  photos,
  onDelete,
  loading,
  onReorder,
}) => {
  const intl = useIntl();
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);

  const movePhoto = (fromIndex: number, toIndex: number) => {
    if (!onReorder) return;
    const newPhotos = [...photos];
    const [movedPhoto] = newPhotos.splice(fromIndex, 1);
    newPhotos.splice(toIndex, 0, movedPhoto);
    onReorder(newPhotos);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      movePhoto(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return photos.length > 0 ? (
    <div className={styles.self}>
      {photos.map((photo, index) => {
        const safeName = typeof photo?.name === 'string' ? photo.name.trim() : '';
        if (!photo || !safeName) return null;
        return (
          <div
            key={safeName || photo.url || index}
            className={`${styles.upload} ${draggedIndex === index ? styles.dragging : ""}`}
            style={{ backgroundImage: `url(${photo.thumbnailUrl || photo.url})` }}
            draggable={!!onReorder}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <Icon
              name={"close"}
              size={"large"}
              aria-label={intl.formatMessage({
                id: "site.imageUpload.removeImage",
                defaultMessage: "Remove image",
              })}
              className={styles.close}
              title={intl.formatMessage({
                id: "site.imageUpload.removeImage",
                defaultMessage: "Remove image",
              })}
              onClick={() => onDelete(photo)}
            />
            {onReorder && (
              <div className={styles.moveButtons}>
                {index > 0 && (
                  <Icon
                    name="arrow left"
                    className={styles.moveButton}
                    title="Move left"
                    onClick={() => movePhoto(index, index - 1)}
                  />
                )}
                {index < photos.length - 1 && (
                  <Icon
                    name="arrow right"
                    className={styles.moveButton}
                    title="Move right"
                    onClick={() => movePhoto(index, index + 1)}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  ) : null;
};

export default ImagesPreview;
